/**
 * Curated location options for the job-create typeahead.
 * Values are stored as-is on wizard_answers.location.
 *
 * Hybrid roles store: `Hybrid · City, Country`
 */

export const HYBRID_SEP = " · ";

export const CITY_OPTIONS: string[] = [
  // Saudi Arabia
  "Riyadh, Saudi Arabia",
  "Jeddah, Saudi Arabia",
  "Dammam, Saudi Arabia",
  "Khobar, Saudi Arabia",
  "Dhahran, Saudi Arabia",
  "Mecca, Saudi Arabia",
  "Medina, Saudi Arabia",
  "Tabuk, Saudi Arabia",
  "Abha, Saudi Arabia",
  "NEOM, Saudi Arabia",
  // UAE & Gulf
  "Dubai, United Arab Emirates",
  "Abu Dhabi, United Arab Emirates",
  "Sharjah, United Arab Emirates",
  "Doha, Qatar",
  "Manama, Bahrain",
  "Kuwait City, Kuwait",
  "Muscat, Oman",
  // Levant & Egypt
  "Amman, Jordan",
  "Cairo, Egypt",
  "Alexandria, Egypt",
  "Beirut, Lebanon",
  "Ramallah, Palestine",
  // Europe
  "London, United Kingdom",
  "Manchester, United Kingdom",
  "Berlin, Germany",
  "Munich, Germany",
  "Amsterdam, Netherlands",
  "Paris, France",
  "Dublin, Ireland",
  "Stockholm, Sweden",
  "Zurich, Switzerland",
  "Madrid, Spain",
  "Barcelona, Spain",
  "Lisbon, Portugal",
  "Warsaw, Poland",
  // North America
  "New York, United States",
  "San Francisco, United States",
  "Los Angeles, United States",
  "Seattle, United States",
  "Austin, United States",
  "Chicago, United States",
  "Boston, United States",
  "Toronto, Canada",
  "Vancouver, Canada",
  "Montreal, Canada",
  // Asia & Oceania
  "Singapore, Singapore",
  "Hong Kong, Hong Kong",
  "Tokyo, Japan",
  "Seoul, South Korea",
  "Bangalore, India",
  "Mumbai, India",
  "Hyderabad, India",
  "Delhi, India",
  "Karachi, Pakistan",
  "Lahore, Pakistan",
  "Islamabad, Pakistan",
  "Sydney, Australia",
  "Melbourne, Australia",
  // Africa
  "Nairobi, Kenya",
  "Lagos, Nigeria",
  "Cape Town, South Africa",
  "Johannesburg, South Africa",
];

export const LOCATION_OPTIONS: string[] = [
  "Remote",
  "Hybrid",
  ...CITY_OPTIONS,
];

export function formatHybridLocation(city: string): string {
  return `Hybrid${HYBRID_SEP}${city.trim()}`;
}

/** Detect Hybrid / Hybrid · City (also accepts -, –, — separators). */
export function parseHybridLocation(value: string | null | undefined): {
  isHybrid: boolean;
  city: string | null;
} {
  const v = value?.trim() ?? "";
  if (!v) return { isHybrid: false, city: null };
  if (/^hybrid$/i.test(v)) return { isHybrid: true, city: null };
  const m = v.match(/^hybrid\s*[·•\-–—]\s*(.+)$/i);
  if (m) return { isHybrid: true, city: m[1].trim() || null };
  return { isHybrid: false, city: null };
}

function canonicalCity(value: string): string | null {
  const v = value.trim().toLowerCase();
  return CITY_OPTIONS.find((opt) => opt.toLowerCase() === v) ?? null;
}

/** Case-insensitive substring match; Remote/Hybrid floated when matched. */
export function searchLocations(query: string, limit = 8): string[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return LOCATION_OPTIONS.slice(0, limit);
  }
  const matched = LOCATION_OPTIONS.filter((opt) =>
    opt.toLowerCase().includes(q),
  );
  return matched.slice(0, limit);
}

/** City-only search for the Hybrid office picker. */
export function searchCities(query: string, limit = 8): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return CITY_OPTIONS.slice(0, limit);
  return CITY_OPTIONS.filter((opt) => opt.toLowerCase().includes(q)).slice(
    0,
    limit,
  );
}

export function isKnownLocation(value: string | null | undefined): boolean {
  const v = value?.trim();
  if (!v) return false;
  if (/^remote$/i.test(v)) return true;
  const hybrid = parseHybridLocation(v);
  if (hybrid.isHybrid) {
    return Boolean(hybrid.city && canonicalCity(hybrid.city));
  }
  return Boolean(canonicalCity(v));
}

/** Normalize casing to the canonical option string when matched. */
export function canonicalLocation(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (/^remote$/i.test(v)) return "Remote";
  const hybrid = parseHybridLocation(v);
  if (hybrid.isHybrid) {
    if (!hybrid.city) return null;
    const city = canonicalCity(hybrid.city);
    return city ? formatHybridLocation(city) : null;
  }
  return canonicalCity(v);
}
